import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { listActivityRefs } from '../api/time-master-data.api';
import { ModeBanner } from '../components/ModeBanner';
import { useDataSourceMode } from '../hooks/useDataSourceMode';
import type { TimeActivityRef, PaginatedResponse } from '../types/time-master-data.types';

/**
 * AP-09 / AP-13 — Liste der TimeActivityRef-Datensätze inkl. CRUD-Aktionen.
 * Buttons sind nur aktiv, wenn DataSourceMode.activityTypeSource ≠ EXTERNAL_API.
 */
export default function TimeActivityRefsPage() {
  const [search, setSearch] = useState('');
  const [data, setData] = useState<PaginatedResponse<TimeActivityRef> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { canEdit } = useDataSourceMode('activity');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listActivityRefs({ search: search || undefined, page: 1, limit: 50 })
      .then(res => { if (!cancelled) { setData(res); setError(null); } })
      .catch(e => { if (!cancelled) setError(e?.message ?? 'Fehler beim Laden'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [search]);

  const rows = useMemo(() => data?.data ?? [], [data]);

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Tätigkeiten (Zeiterfassung)</h1>
          <p className="text-sm text-muted-foreground">
            Hier kategorisierst du einzelne Zeitbuchungen.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Suchen (Name, externalId)"
            className="border rounded px-3 py-2 text-sm w-72"
          />
          {canEdit ? (
            <Link
              to="/time-tracking/activity-types/new"
              className="px-3 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:opacity-90"
            >
              + Neu
            </Link>
          ) : (
            <button
              type="button"
              disabled
              title="Nicht verfügbar, weil diese Daten aus deinem anderen Programm kommen."
              className="px-3 py-2 text-sm rounded-md bg-primary text-primary-foreground opacity-40 cursor-not-allowed"
            >
              + Neu
            </button>
          )}
        </div>
      </header>

      <ModeBanner kind="activity" />

      {loading && <div className="text-sm text-muted-foreground">Wird geladen …</div>}
      {error && <div className="text-sm text-red-600">{error}</div>}

      {!loading && !error && (
        <div className="overflow-x-auto border rounded">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="text-left px-3 py-2">Sortierung</th>
                <th className="text-left px-3 py-2">Bezeichnung</th>
                <th className="text-left px-3 py-2">Farbe</th>
                <th className="text-left px-3 py-2">Quelle</th>
                <th className="text-right px-3 py-2">Custom Fields</th>
                <th className="text-right px-3 py-2">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center px-3 py-6 text-muted-foreground">
                    Keine Datensätze.
                  </td>
                </tr>
              )}
              {rows.map(r => (
                <tr key={r.id} className="border-t">
                  <td className="px-3 py-2 font-mono">{r.sortOrder}</td>
                  <td className="px-3 py-2">{r.name}</td>
                  <td className="px-3 py-2">
                    {r.color
                      ? <span className="inline-flex items-center gap-2">
                          <span
                            className="inline-block w-4 h-4 rounded border"
                            style={{ backgroundColor: r.color }}
                          />
                          <span className="font-mono text-xs">{r.color}</span>
                        </span>
                      : <span className="text-muted-foreground">–</span>}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {r.externalSource
                      ? <span title={r.externalId ?? ''}>{r.externalSource} · {r.externalId}</span>
                      : <span className="text-muted-foreground">manuell</span>}
                  </td>
                  <td className="px-3 py-2 text-right text-xs text-muted-foreground">
                    {r.customFields ? Object.keys(r.customFields).length + ' Felder' : '–'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link
                      to={`/time-tracking/activity-types/${r.id}/edit`}
                      className="text-xs border rounded px-2 py-1 hover:bg-accent"
                    >
                      Bearbeiten
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && (
        <div className="text-xs text-muted-foreground">
          Seite {data.meta.page} / {data.meta.totalPages} – insgesamt {data.meta.total} Einträge
        </div>
      )}
    </div>
  );
}
